import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Container, useTheme, useMediaQuery } from "@mui/material";
import { styled } from "@mui/system";
import { FaGraduationCap, FaBook, FaPencilAlt } from "react-icons/fa";
import LogoImage from "../assets/images/logo.jpeg";


const LoadingWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isClosing"
})<{ isClosing: boolean }>(({ theme, isClosing }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
background: theme.palette.mode === "dark"
  ? `linear-gradient(135deg, #C92BB5 0%, #6D1B77 50%, #120A39 100%)`
  : `linear-gradient(135deg, #FBF3FD 0%, #EBD0F2 100%)`,
  position: "fixed", // legyen lefedő
  width: "100vw",
  height: "100vh",
  zIndex: 2000,
  overflow: "hidden",
  top: 0,
  left: 0,
  transition: "clip-path 1s ease-in-out, opacity 0.5s ease-in-out",
  clipPath: isClosing ? "circle(0% at 50% 50%)" : "circle(150% at 50% 50%)",
  opacity: isClosing ? 0 : 1,
  pointerEvents: isClosing ? "none" : "auto"
}));


const FloatingIcon = styled(Box)(({ theme }) => ({
  position: "absolute",
  animation: "float 6s ease-in-out infinite",
  opacity: 0.25,
  color: theme.palette.mode === "dark" ? "#FCC632" : "#6D1B77",
  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0)"
    },
    "50%": {
      transform: "translateY(-20px)"
    }
  }
}));

const ProgressWrapper = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(3)
}));

const quotes = [
  "Az oktatás az útlevél a jövőbe",
  "A tanulás egy kincs, amely mindenhová követi tulajdonosát",
  "A tudás hatalom",
  "A tanulásban az a gyönyörű, hogy senki sem veheti el tőled",
  "Az oktatás nem felkészülés az életre; az oktatás maga az élet"
];

interface LoadingPageProps {
  stage: "LOADING" | "FINISHING";
}

const LoadingPage: React.FC<LoadingPageProps> = ({ stage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [quote, setQuote] = useState("");
  const [isClosing, setIsClosing] = useState(false);

useEffect(() => {
  setIsClosing(stage === "FINISHING");
}, [stage]);


  useEffect(() => {
    const randomQuote = quotes[Math.floor(Math.random() * quotes.length)];
    setQuote(randomQuote);

    const quoteTimer = setInterval(() => {
      const newQuote = quotes[Math.floor(Math.random() * quotes.length)];
      setQuote(newQuote);
    }, 4000);

    return () => clearInterval(quoteTimer);
  }, []);

  return (
    <LoadingWrapper isClosing={isClosing}>
      <FloatingIcon sx={{ top: "10%", left: "10%" }}>
        <FaBook size={isMobile ? 30 : 50} />
      </FloatingIcon>
      <FloatingIcon sx={{ top: "20%", right: "15%" }}>
        <FaPencilAlt size={isMobile ? 25 : 40} />
      </FloatingIcon>
      <FloatingIcon sx={{ bottom: "15%", left: "20%" }}>
        <FaGraduationCap size={isMobile ? 35 : 60} />
      </FloatingIcon>

      <Container maxWidth="sm">
        <ProgressWrapper>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
            variant="indeterminate"
            size={isMobile ? 100 : 120}
            thickness={4}
            sx={{
                color: theme.palette.primary.main,
                animationDuration: "1.2s"
            }}
            />

            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <Box
        component="img"
        src={LogoImage}
        alt="Csillagtanoda Logo"
        sx={{
          width: isMobile ? 40 : 50,
          height: isMobile ? 40 : 50,
         animation: "rotate 2s linear infinite",
         filter: theme.palette.mode === "dark"
            ? "brightness(1.2)"
            : "none"
        }}
      />
            </Box>
          </Box>

          <Typography
            variant={isMobile ? "h6" : "h5"}
            sx={{
              color: theme.palette.primary.main,
              textAlign: "center",
              fontWeight: "bold",
              animation: "fade 2s ease-in-out infinite"
            }}
          >
            Tudás betöltése....
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: theme.palette.mode === "dark"
                ? theme.palette.primary.light
                : theme.palette.primary.main,
              textAlign: "center",
              maxWidth: "80%",
              margin: "0 auto",
              opacity: 0.8
            }}
          >
            {quote}
          </Typography>
        </ProgressWrapper>
      </Container>

      <style>
        {`
          @keyframes rotate {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }
          @keyframes fade {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.7; }
          }
        `}
      </style>
    </LoadingWrapper>
  );
};

export default LoadingPage;